// Line-level identity classification shared by the launch refusal
// (draftGuards) and the uploaded-script composer (campaignComposer). It lives
// on its own because those two modules already point at each other: the
// refusal reads message bodies through the composer, so the composer must not
// import the refusal back.

export const CAMPAIGN_VALEDICTION_SOURCE = String.raw`appreciatively|best(?:\s+regards)?|cheers|cordially|faithfully|fondly|gratefully|kindly|regards|respectfully|sincerely|thank(?:s|\s+you)(?:\s+again)?|truly|warmly|yours\s+(?:faithfully|respectfully|sincerely|truly|warmly)|very\s+truly\s+yours|(?:with\s+)?(?:best|kind|kindest|many|sincere|warm|warmest)\s+(?:appreciation|gratitude|regards|thanks|wishes)|with\s+(?:appreciation|gratitude|regards|thanks|wishes)`;
export const CAMPAIGN_VALEDICTION_LINE_PATTERN = new RegExp(
  `^(?:${CAMPAIGN_VALEDICTION_SOURCE})[,.!?:;]?$`,
  'iu'
);
export const CAMPAIGN_PERSON_IDENTITY_PATTERN = /^(?:\p{Lu}[\p{L}\p{M}.'’–-]*)(?:\s+\p{Lu}[\p{L}\p{M}.'’–-]*){1,5}[,.]?$/u;
export const CAMPAIGN_SINGLE_IDENTITY_PATTERN = /^(?!(?:access|availability|afternoon|broker|cam|contact|cre|details|docks|evening|floorplan|loading|morning|noi|parking|power|property|rent|sf|today|tomorrow|tonight|total)[,.]?$)\p{Lu}[\p{L}\p{M}.'’–-]{0,79}[,.]?$/iu;
export const CAMPAIGN_ROLE_DETAIL_PATTERN = /^(?:c[a-z]{1,3}o|[a-z]?vp|advisor|agent|analyst|associate|broker|chair(?:man|person)?|chief|coordinator|director|executive|founder|head|lead|leader|manager|member|officer|owner|partner|president|principal|rep(?:resentative)?|specialist)(?:\b|[\s,/&-])/iu;
export const CAMPAIGN_BUSINESS_DETAIL_PATTERN = /\b(?:associates?|capital|co(?:mpany)?|commercial|corp(?:oration)?|cre|group|holdings?|inc(?:orporated)?|investments?|limited|llc|llp|lp|ltd|partners?|properties|real estate|realty|services|ventures?)\b/iu;
export const CAMPAIGN_CONTACT_DETAIL_PATTERN = /(?:[\w.+-]+@[\w.-]+\.[a-z]{2,}|(?:https?:\/\/|www\.)\S+|(?:\+?\d|\(\d{2,4}\))[\d\s().-]{5,}\d|(?:phone|mobile|cell|tel(?:ephone)?|direct|office|fax)\s*[.:]?\s*(?:\+?\d|\(\d{2,4}\)))/iu;
export const CAMPAIGN_PROFESSIONAL_DETAIL_PATTERN = /\b(?:accounts?|advisory|analytics|brokerage|capital markets|debt|equity|finance|industrial|leasing|logistics|office|operations|practice|representation|research|sales|strategy|transactions?|valuation|workplace)\b/iu;
export const CAMPAIGN_BODY_FIELD_DETAIL_PATTERN = /\b(?:access|address|amount|broker|cam|contact|cre|date|details|docks|doors|expenses|floorplan|height|ht|improvements|information|ins|link|metric|metrics|name|noi|owner|overview|plan|power|rent|rep|sf|size|spaces|steps|summary|term|type|year|zoning)$/iu;
export const CAMPAIGN_SIGNATURE_MARKER_PATTERN = /(?:data-sitesift-professional-signature|sitesift:professional-signature)/iu;
export const CAMPAIGN_BODY_LIKE_VALEDICTION_PATTERN = /^thank(?:s|\s+you)?\s+again[,.!?:;]?$/iu;
export const CAMPAIGN_WEAK_GREETING_BODY_PROSE_PATTERN = /^(?:morning|afternoon|evening)\s+[^\r\n,;:–—]{0,100}\b(?:aligns?|appears?|are|begins?|can|could|does|fills?|has|have|helps?|is|matters?|needs?|remains?|requires?|should|starts?|was|were|will|works?|would)\b/iu;

export function normalizeCampaignBodyFieldLabel(value) {
  return String(value || '')
    .trim()
    .replace(/:$/u, '')
    .replace(/\s+/gu, ' ')
    .toLocaleLowerCase();
}

export function isConfiguredCampaignBodyFieldLabel(value, bodyFieldLabels = []) {
  const normalized = normalizeCampaignBodyFieldLabel(value);
  return Array.isArray(bodyFieldLabels) && bodyFieldLabels.some(
    label => normalizeCampaignBodyFieldLabel(label) === normalized
  );
}

export function looksLikeCampaignPersonIdentity(value, bodyFieldLabels = []) {
  const text = String(value || '').trim();
  return !isConfiguredCampaignBodyFieldLabel(text, bodyFieldLabels)
    && !CAMPAIGN_BODY_FIELD_DETAIL_PATTERN.test(text)
    && !CAMPAIGN_PROFESSIONAL_DETAIL_PATTERN.test(text)
    && CAMPAIGN_PERSON_IDENTITY_PATTERN.test(text);
}

export function looksLikeCampaignIdentityDetail(value) {
  const text = String(value || '').trim();
  return CAMPAIGN_ROLE_DETAIL_PATTERN.test(text)
    || CAMPAIGN_BUSINESS_DETAIL_PATTERN.test(text)
    || CAMPAIGN_PROFESSIONAL_DETAIL_PATTERN.test(text)
    || CAMPAIGN_CONTACT_DETAIL_PATTERN.test(text);
}

export function looksLikeCampaignSingleIdentity(value, bodyFieldLabels = []) {
  const text = String(value || '').trim();
  return !isConfiguredCampaignBodyFieldLabel(text, bodyFieldLabels)
    && CAMPAIGN_SINGLE_IDENTITY_PATTERN.test(text);
}

export function looksLikeCampaignBusinessIdentity(value, bodyFieldLabels = []) {
  const text = String(value || '').trim();
  return !isConfiguredCampaignBodyFieldLabel(text, bodyFieldLabels)
    && !CAMPAIGN_BODY_FIELD_DETAIL_PATTERN.test(text)
    && CAMPAIGN_BUSINESS_DETAIL_PATTERN.test(text);
}


export function isCampaignSignOffLine(line) {
  const text = String(line || '').trim();
  return CAMPAIGN_VALEDICTION_LINE_PATTERN.test(text)
    && !CAMPAIGN_BODY_LIKE_VALEDICTION_PATTERN.test(text);
}

export const CAMPAIGN_LAUNCH_GREETING_SOURCE = String.raw`good\s+(?:morning|afternoon|evening|day)|greetings|hello|dear|hey|hi|morning|afternoon|evening`;
export const CAMPAIGN_GREETING_RECIPIENT_SEPARATOR = String.raw`(?:\s+|\s*[,!?.:;–—]\s*)`;
export const CAMPAIGN_GREETING_ONLY_PATTERN = new RegExp(
  `^\\s*(?:${CAMPAIGN_LAUNCH_GREETING_SOURCE})${CAMPAIGN_GREETING_RECIPIENT_SEPARATOR}(?:\\[NAME\\]|Sir\\/Madam|Leasing Agent|Team|there|[\\p{L}\\p{N}][\\p{L}\\p{M}\\p{N}._%'+-]*(?:\\s+[\\p{L}\\p{N}][\\p{L}\\p{M}\\p{N}._%'+-]*){0,4})\\s*[,!?.:;–—-]*\\s*$`,
  'iu'
);
export const CAMPAIGN_BARE_GREETING_ONLY_PATTERN = new RegExp(
  `^\\s*(?:${CAMPAIGN_LAUNCH_GREETING_SOURCE})\\s*[,!?.:;–—-]*\\s*$`,
  'iu'
);
// A bare greeting, a blank line, and then a LITERAL ADDRESSEE standing where
// the greeting's name belongs ("Hi,\n\nJordan Smith,"). The structure alone is
// not enough: every ordinary message that opens "Hi," and then a sentence has
// exactly this shape, and matching on the shape alone refused four paragraphs
// of real copy with "Add message content after the greeting." (R18 item 27).
// The captured line must also READ as a name.
const CAMPAIGN_BLANK_WRAPPER_STRUCTURE_PATTERN = new RegExp(
  `^\\s*(?:${CAMPAIGN_LAUNCH_GREETING_SOURCE})\\s*[,!?.:;–—-]+[\\t ]*\\r?\\n[\\t ]*\\r?\\n[\\t ]*([^\\r\\n]+(?:[,!?.]|[–—]))[\\t ]*(?:(?:\\r?\\n[\\t ]*){2}[\\s\\S]*)?$`,
  'iu'
);

export function isCampaignBlankWrapperLiteralRecipient(value) {
  const match = String(value || '').match(CAMPAIGN_BLANK_WRAPPER_STRUCTURE_PATTERN);
  if (!match) return false;

  const literalLine = match[1].replace(/[,!?.:;–—-]+$/u, '').trim();
  if (!literalLine) return false;

  // The raw shape patterns, not the looksLike* wrappers: the server mirror in
  // functions/lib/campaignLaunchSafety.js has no Ask contract to consult, and
  // the two launch boundaries must refuse the same bodies.
  return CAMPAIGN_PERSON_IDENTITY_PATTERN.test(literalLine)
    || CAMPAIGN_SINGLE_IDENTITY_PATTERN.test(literalLine);
}

/**
 * The launch gate's "this is a greeting with an addressee and nothing else"
 * shape. It is the one thing the uploaded-script composer must NOT rewrite: a
 * message whose whole content is "Hi Thanks." cannot be repaired by attaching
 * a placeholder, so it stays refused rather than being turned into a sendable
 * message that says nothing.
 */
export function isCampaignGreetingWithoutMessage(value) {
  const text = String(value || '');
  return CAMPAIGN_GREETING_ONLY_PATTERN.test(text)
    || CAMPAIGN_BARE_GREETING_ONLY_PATTERN.test(text);
}

export function isCampaignGreetingOnlyMessage(value) {
  const text = String(value || '');
  return CAMPAIGN_GREETING_ONLY_PATTERN.test(text)
    || CAMPAIGN_BARE_GREETING_ONLY_PATTERN.test(text)
    || isCampaignBlankWrapperLiteralRecipient(text);
}
